import _, {Dictionary} from 'lodash';
import React, {FunctionComponent, useState} from 'react';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {Collapse, IconButton} from '@material-ui/core';
import {BackIcon} from '@treez-ui/core/icons';
import {Title, TitleSize} from '@treez-ui/core/layout/Title';
import AdvancedProductFilter from '@treez-ui/core/modules/AdvancedProductFilter';
import {ProductTypePicker} from '@treez-ui/core/pickers';
import {Color, ColorName} from '@treez-ui/core/styles/Color';
import {ProductType} from '@treez/commons/sharedTypings/product';
import {
    brandTreezEffectOptions,
    brandTreezFlavorOptions,
    brandTreezGeneralAttributeOptions,
    brandTreezIngredientOptions,
} from '@treez/commons/utils/BrandTreez';
import {ProductListView} from '~/lib/product';
import {ProductFilters} from '~/lib/sharedInterfaces';
import SearchBar from '~/modules/forms/SearchBar';
import FiltersButton from '~/modules/widgets/FiltersButton';
import ProductViewOptions from '~/modules/widgets/ProductViewOptions';
import {selectBrandTreezBrands} from '~/redux/selectors/ProductMetaDataSelectors';

const {TABLE, TILE} = ProductListView;

interface Props {
    currentView: ProductListView;
    filters: ProductFilters;
    searchTerm: string;
    applyFilters: (filtersApplied: ProductFilters) => void;
    changeView: (newView: ProductListView) => void;
    updateSearchTerm: (newSearchTerm: string) => void;
}

export const BrandTreezNavTools: FunctionComponent<Props> = (props: Props) => {
    const {currentView, filters, searchTerm, applyFilters, changeView, updateSearchTerm} = props;

    const history = useHistory();

    const [filterMenuOpen, setFilterMenuOpen] = useState<boolean>(false);

    const handleProductTypeSelection = (productTypesSelected: ProductType[]) => {
        applyFilters({
            ...filters,
            types: productTypesSelected,
        });
    };

    const handleApplyAdvancedFilters = (filtersApplied: Dictionary<string[]>) => {
        applyFilters(filtersApplied);
    };

    const productTypesSelected: ProductType[] = (filters.types as ProductType[]) || [];
    const brandOptions = useSelector(selectBrandTreezBrands);

    return (
        <div className="brand-treez-nav-tools-container">
            <div className="brand-treez-nav-tools">
                <div className="brand-treez-top-nav-bar">
                    <IconButton onClick={() => history.goBack()}>
                        <BackIcon color={Color[ColorName.WHITE]} />
                    </IconButton>
                </div>
                <Title
                    text="WELCOME TO THE UNIVERSAL CANNABIS PRODUCT CATALOG"
                    size={TitleSize.MEDIUM}
                />
                <Title text={`EXPLORE THE INDUSTRY'S TOP BRANDS`} size={TitleSize.LARGE} />
                <div className="browse-treez-search-tools">
                    <div className="search-bar-container">
                        <SearchBar searchTerm={searchTerm || ''} setSearchTerm={updateSearchTerm} />
                    </div>
                    <div className="tool-bar-container">
                        <div className="options-parent-container">
                            <div className="standard-button-container">
                                <FiltersButton
                                    advancedFiltersOpen={filterMenuOpen}
                                    filtersApplied={_.flatten(_.values(filters)).length > 0}
                                    toggleFilterMenuOpen={() => setFilterMenuOpen(!filterMenuOpen)}
                                />
                            </div>
                            <ProductTypePicker
                                selected={productTypesSelected}
                                setSelected={handleProductTypeSelection}
                            />
                        </div>
                        <ProductViewOptions
                            currentView={currentView}
                            setProductView={changeView}
                            viewOptions={[TABLE, TILE]}
                        />
                    </div>
                </div>
            </div>
            <div className="advanced-filter-collapse-container">
                <Collapse in={filterMenuOpen}>
                    <div className="advanced-filter-container">
                        <AdvancedProductFilter
                            brands={brandOptions}
                            effects={brandTreezEffectOptions}
                            ingredients={brandTreezIngredientOptions}
                            generalAttributes={brandTreezGeneralAttributeOptions}
                            filters={filters as Dictionary<string[] | undefined>}
                            internalTags={[]}
                            onChange={handleApplyAdvancedFilters}
                            flavors={brandTreezFlavorOptions}
                        />
                    </div>
                </Collapse>
            </div>
        </div>
    );
};

export default BrandTreezNavTools;
